import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InfoHint } from "@/components/InfoHint";
import { HINTS } from "@/lib/credentialHints";
import api, { formatApiErrorDetail } from "@/lib/api";

function Field({ label, hint, hintTestid, ...props }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</Label>
        {hint && <InfoHint text={hint} testid={hintTestid} />}
      </div>
      <Input className="font-mono text-sm" {...props} />
    </div>
  );
}

const empty = { name: "", ghlId: "", tab: "later", appId: "", locId: "", token: "", env: "sandbox", usePlatform: true, secret: "", pub: "" };

export default function SubAccountDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    let sub = null;
    try {
      const res = await api.post("/subaccounts", { name: f.name, ghl_subaccount_id: f.ghlId });
      sub = res.data;
      let final = sub;
      if (f.tab === "square" && f.appId && f.locId && f.token) {
        const { data } = await api.put(`/subaccounts/${sub.id}/square`, {
          application_id: f.appId, location_id: f.locId, access_token: f.token, environment: f.env,
        });
        final = data;
      } else if (f.tab === "stripe") {
        const payload = f.usePlatform ? { use_platform: true } : { use_platform: false, secret_key: f.secret, publishable_key: f.pub };
        const { data } = await api.put(`/subaccounts/${sub.id}/stripe`, payload);
        final = data;
      }
      toast.success("Sub-account created");
      setF(empty);
      onOpenChange(false);
      onCreated(final);
    } catch (err) {
      if (sub) {
        toast.warning("Account created, but saving credentials failed — add them in the Providers tab.");
        setF(empty);
        onOpenChange(false);
        onCreated(sub);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const disabled =
    busy || !f.name || !f.ghlId ||
    (f.tab === "square" && (!f.appId || !f.locId || !f.token)) ||
    (f.tab === "stripe" && !f.usePlatform && !f.secret);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">New Sub-Account</DialogTitle>
          <DialogDescription>Connect a GHL sub-account and its payment credentials.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Display Name</Label>
            <Input data-testid="subaccount-name-input" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Acme Store" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">GHL Sub-Account ID</Label>
            <Input data-testid="subaccount-ghlid-input" value={f.ghlId} onChange={(e) => set("ghlId", e.target.value)} placeholder="e.g. ve9EPM428h8vShlRW1KT" className="font-mono" />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-2 block">Payment Provider</Label>
            <Tabs value={f.tab} onValueChange={(v) => set("tab", v)}>
              <TabsList className="bg-secondary w-full">
                <TabsTrigger value="square" data-testid="create-tab-square" className="flex-1">Square</TabsTrigger>
                <TabsTrigger value="stripe" data-testid="create-tab-stripe" className="flex-1">Stripe</TabsTrigger>
                <TabsTrigger value="later" data-testid="create-tab-later" className="flex-1">Later</TabsTrigger>
              </TabsList>

              <TabsContent value="square" className="mt-4 space-y-3">
                <Field label="Application ID" hint={HINTS.square_app_id} hintTestid="create-square-appid-hint" data-testid="square-appid-input" value={f.appId} onChange={(e) => set("appId", e.target.value)} placeholder="sandbox-sq0idb-..." />
                <Field label="Location ID" hint={HINTS.square_location_id} hintTestid="create-square-locid-hint" data-testid="square-locid-input" value={f.locId} onChange={(e) => set("locId", e.target.value)} placeholder="L..." />
                <Field label="Access Token" hint={HINTS.square_access_token} hintTestid="create-square-token-hint" data-testid="square-token-input" type="password" value={f.token} onChange={(e) => set("token", e.target.value)} placeholder="EAAAl..." />
                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Environment</Label>
                    <InfoHint text={HINTS.square_env} testid="create-square-env-hint" />
                  </div>
                  <Select value={f.env} onValueChange={(v) => set("env", v)}>
                    <SelectTrigger data-testid="square-env-select" className="font-mono text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="stripe" className="mt-4 space-y-3">
                <div className="flex items-center justify-between rounded-md border border-border p-3">
                  <div>
                    <div className="text-sm font-medium">Use built-in test key</div>
                    <div className="text-xs text-muted-foreground">Platform Stripe sandbox — no account needed</div>
                  </div>
                  <Switch data-testid="stripe-platform-switch" checked={f.usePlatform} onCheckedChange={(v) => set("usePlatform", v)} />
                </div>
                {!f.usePlatform && (
                  <>
                    <Field label="Secret Key" hint={HINTS.stripe_secret} hintTestid="create-stripe-secret-hint" data-testid="stripe-secret-input" type="password" value={f.secret} onChange={(e) => set("secret", e.target.value)} placeholder="sk_test_... / sk_live_..." />
                    <Field label="Publishable Key" hint={HINTS.stripe_pub} hintTestid="create-stripe-pub-hint" data-testid="stripe-pub-input" value={f.pub} onChange={(e) => set("pub", e.target.value)} placeholder="pk_..." />
                  </>
                )}
              </TabsContent>

              <TabsContent value="later" className="mt-4">
                <p className="text-sm text-muted-foreground">You can connect Square or Stripe later from the sub-account's Providers tab.</p>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter>
          <Button data-testid="subaccount-create-submit" onClick={submit} disabled={disabled}>
            {busy ? "Creating…" : "Create Sub-Account"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
